// TODO: This file needs a refactoring, it should use the variant object syntax
import TextareaAutosize from 'react-textarea-autosize';
import styled, { css } from 'styled-components';

import { IconTick, IconWarning } from '@xing-com/icons';
import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import {
  cornerRadiusM,
  scale005,
  scale010,
  scale050,
  scale060,
  scale070,
  scale080,
  scale150,
  scale160,
  scale170,
  spaceL,
  spaceM,
  spaceS,
  spaceXS,
  spaceXXS,
  xdlColorControlBorderSecondary,
  xdlColorControlFill,
  xdlColorA11yBorder,
  xdlColorControlBorder,
  xdlColorControlFillSecondary,
  xdlColorDisabledSoft,
  xdlColorDisabledStrong,
  xdlColorError,
  xdlColorFocusRing,
  xdlColorSuccess,
  xdlColorText,
  xdlColorTextSecondary,
} from '@xing-com/tokens';
import { Meta } from '@xing-com/typography';

import type { FormFieldSize, FormFieldVariant } from './form-field.types';

type ActionWrapperTProps = {
  $hasFocus?: boolean;
  $isDisabled?: boolean;
};

type TextareaTProps = {
  $error?: boolean;
  $isDisabled?: boolean;
  $isFilled: boolean;
  $size?: FormFieldSize;
  $sizeConfined?: FormFieldSize;
  $sizeWide?: FormFieldSize;
  $success?: boolean;
  $variant?: FormFieldVariant;
};

type InputTProps = {
  $error?: boolean;
  $hasAction?: boolean;
  $iconSize?: 18 | 24;
  $isDisabled?: boolean;
  $isFilled: boolean;
  $isIconSet?: boolean;
  $size?: FormFieldSize;
  $sizeConfined?: FormFieldSize;
  $sizeWide?: FormFieldSize;
  $success?: boolean;
  $variant?: FormFieldVariant;
};

type LabelTProps = {
  $hasFocus: boolean;
  $isDisabled?: boolean;
  $isFilled: boolean;
  $isIconSet?: boolean;
  $size: FormFieldSize;
  $sizeConfined?: FormFieldSize;
  $sizeWide?: FormFieldSize;
};

type HelperTextTProps = {
  $error?: boolean;
  $isDisabled?: boolean;
};

type IconContainerTProps = {
  $hasFocus?: boolean;
  $iconSize?: 18 | 24;
  $isDisabled?: boolean;
};

export const Wrapper = styled.div`
  position: relative;
`;

export const InputWrapper = styled.div`
  position: relative;
`;

// *** Base Styles ***
const styles = css`
  background-color: ${xdlColorControlFillSecondary};
  border-block-end: ${scale010} solid ${xdlColorControlBorder};
  border-block-start: ${scale005} solid ${xdlColorA11yBorder};
  border-inline-end: ${scale005} solid ${xdlColorA11yBorder};
  border-inline-start: ${scale005} solid ${xdlColorA11yBorder};
  border-radius: ${cornerRadiusM} ${cornerRadiusM} 0 0;
  color: ${xdlColorTextSecondary};
  display: inline-block;
  outline: none;
  padding-inline-end: ${spaceM};
  padding-inline-start: ${spaceL};
  width: 100%;

  @media (prefers-contrast: more) {
    & {
      border-block-end-width: 3px !important;
      border-block-start-width: ${scale010} !important;
      border-inline-end-width: ${scale010} !important;
      border-inline-start-width: ${scale010} !important;
    }
  }

  &:focus {
    background-color: ${xdlColorControlFill};
    border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};
    border-block-start: ${scale005} solid ${xdlColorControlBorder};
    border-inline-end: ${scale005} solid ${xdlColorControlBorder};
    border-inline-start: ${scale005} solid ${xdlColorControlBorder};
    outline-offset: ${spaceXXS};
    outline: ${scale010} solid ${xdlColorFocusRing};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:hover {
    border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};

    @media (prefers-contrast: more) {
      border-width: ${scale010};
      box-shadow: inset 0 0 0 ${scale005} ${xdlColorControlBorderSecondary};
    }
  }
`;

// *** sizes ***
export const sizes = {
  small: {
    input: css`
      font-size: ${scale070};
      height: ${scale150};
      padding-block-end: ${scale050};
      padding-block-start: ${scale050};
    `,
    label: css`
      font-size: ${scale070};
      inset-block-start: 10px;
    `,
  },
  medium: {
    input: css`
      font-size: ${scale080};
      height: ${scale160};
      padding-block-end: ${scale060};
      padding-block-start: ${scale060};
    `,
    label: css`
      font-size: ${scale080};
      inset-block-start: 12px;
    `,
  },
  large: {
    input: css`
      font-size: ${scale080};
      height: ${scale170};
      padding-block-end: 15px;
      padding-block-start: 15px;
    `,
    label: css`
      font-size: ${scale080};
      inset-block-start: 15px;
    `,
  },
};

// *** State styles ***
const filledStyle = css`
  background-color: ${xdlColorControlFill};
  font-size: ${scale060};
  inset-block-start: -9px;
  inset-inline-start: -4px;
  padding-block-end: 0;
  padding-block-start: 0;
  padding-inline-end: ${spaceXS};
  padding-inline-start: ${spaceXS};
  transform: none;
`;

const inputFilled = css`
  background-color: ${xdlColorControlFill};
  border-block-end: ${scale010} solid ${xdlColorControlBorder};
  border-block-start: ${scale005} solid ${xdlColorControlBorder};
  border-inline-end: ${scale005} solid ${xdlColorControlBorder};
  border-inline-start: ${scale005} solid ${xdlColorControlBorder};
  color: ${xdlColorText};
`;

const errorStyle = css`
  && {
    border-block-end: ${scale010} solid ${xdlColorError};

    &:hover,
    &:focus {
      border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};
    }
  }
`;

const successStyle = css`
  && {
    border-block-end: ${scale010} solid ${xdlColorSuccess};

    &:hover,
    &:focus {
      border-block-end: ${scale010} solid ${xdlColorControlBorderSecondary};
    }
  }
`;

const plainStyles = css`
  && {
    background-color: ${xdlColorControlFill};
    border-block-end: ${scale010} solid ${xdlColorControlBorder};
    border-block-start: ${scale005} solid ${xdlColorControlBorder};
    border-inline-end: ${scale005} solid ${xdlColorControlBorder};
    border-inline-start: ${scale005} solid ${xdlColorControlBorder};

    &:hover,
    &:focus {
      border-block-end-color: ${xdlColorControlBorderSecondary};
    }
  }
`;

const disabledStyle = css`
  border-color: ${xdlColorDisabledSoft} !important;
  color: ${xdlColorDisabledStrong};
  pointer-events: none;
`;

const disabledPlainStyle = css`
  border-block-end: ${spaceXXS} solid ${xdlColorDisabledSoft} !important;
  border-block-start: ${scale005} solid ${xdlColorDisabledSoft} !important;
  border-inline-end: ${scale005} solid ${xdlColorDisabledSoft} !important;
  border-inline-start: ${scale005} solid ${xdlColorDisabledSoft} !important;
  color: ${xdlColorDisabledStrong} !important;
  pointer-events: none;
`;

export const HelperContainer = styled.div`
  display: flex;
  gap: ${spaceS};
  justify-content: space-between;
  margin-block-start: ${spaceXS};
`;

export const CharacterCounterArea = styled(Meta)`
  flex-grow: 1;
  text-align: end;
`;

const helperTextError = css`
  && {
    color: ${xdlColorError};
  }
`;

export const HelperText = styled(Meta)<HelperTextTProps>`
  align-items: center;
  color: ${xdlColorTextSecondary};
  display: flex;
  flex-grow: 1;
  gap: ${spaceXXS};

  ${({ $error }) => $error && helperTextError};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      && {
        color: ${xdlColorDisabledStrong};
      }
    `}
`;

export const ErrorIcon = styled(IconWarning)`
  flex-shrink: 0;
  height: ${scale060};
  width: ${scale060};
`;

export const SucessIcon = styled(IconTick)`
  flex-shrink: 0;
  height: ${scale060};
  width: ${scale060};
`;

export const Input = styled.input<InputTProps>`
  ${styles}

  ${({ $variant }) => $variant === 'plain' && plainStyles};

  ${({ $size }) => $size && sizes[$size].input};
  ${({ $sizeConfined }) =>
    $sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[$sizeConfined].input};
      }
    `};
  ${({ $sizeWide }) =>
    $sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[$sizeWide].input};
      }
    `};

  ${({ $isIconSet }) =>
    $isIconSet &&
    css`
      padding-inline-start: 35px;
    `};
  ${({ $isIconSet, $size }) =>
    $isIconSet &&
    $size === 'medium' &&
    css`
      padding-inline-start: 40px;
    `};
  ${({ $isIconSet, $size }) =>
    $isIconSet &&
    $size === 'large' &&
    css`
      padding-inline-start: 40px;
    `};

  ${({ $hasAction }) =>
    $hasAction &&
    css`
      padding-inline-end: 40px;
    `};

  ${({ $isFilled }) => $isFilled && inputFilled};

  ${({ $success }) => $success && successStyle};
  ${({ $error }) => $error && errorStyle};

  ${({ $isDisabled }) => $isDisabled && disabledStyle};
  ${({ $isDisabled, $variant }) =>
    $isDisabled && $variant === 'plain' && disabledPlainStyle};

  ${({ $isDisabled, $isFilled }) =>
    $isDisabled &&
    !$isFilled &&
    css`
      && {
        border: 0;
      }
    `}

  &:not(:placeholder-shown) ~ &,
  :-webkit-autofill ~ &,
  :autofill ~ & {
    ${filledStyle};
  }
`;

export const Textarea = styled(TextareaAutosize)<TextareaTProps>`
  ${styles}
  height: auto;
  overflow-y: hidden;
  resize: none;
  width: 100%;

  ${({ $variant }) => $variant === 'plain' && plainStyles};

  ${({ $size }) => $size && sizes[$size].input};
  ${({ $sizeConfined }) =>
    $sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[$sizeConfined].input};
      }
    `};
  ${({ $sizeWide }) =>
    $sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[$sizeWide].input};
      }
    `};

  ${({ $isFilled }) => $isFilled && inputFilled};
  ${({ $success }) => $success && successStyle};
  ${({ $error }) => $error && errorStyle};

  ${({ $isDisabled }) => $isDisabled && disabledStyle};
  ${({ $isDisabled, $variant }) =>
    $isDisabled && $variant === 'plain' && disabledPlainStyle};

  ${({ $isDisabled, $isFilled }) =>
    $isDisabled &&
    !$isFilled &&
    css`
      && {
        border: 0;
      }
    `}
`;

export const Label = styled.label<LabelTProps>`
  color: ${xdlColorTextSecondary};
  display: inline-block;
  font-size: ${scale070};
  font-weight: 400;
  inset-inline-start: 0;
  margin-inline-start: ${spaceL};
  pointer-events: none;
  position: absolute;
  transition: all 0.2s ease-in-out;
  z-index: 1;

  ${({ $size }) => $size && sizes[$size].label};

  ${({ $isFilled }) => $isFilled && filledStyle};

  ${({ $hasFocus }) => $hasFocus && filledStyle};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${xdlColorDisabledStrong};
    `}

  ${({ $isFilled, $isIconSet }) =>
    $isFilled &&
    $isIconSet &&
    css`
      inset-inline-start: -25px;
    `};

  ${({ $hasFocus, $isIconSet }) =>
    $hasFocus &&
    $isIconSet &&
    css`
      inset-inline-start: -25px;
    `};

  ${({ $isIconSet }) =>
    $isIconSet &&
    css`
      margin-inline-start: 35px;
    `};

  ${({ $isIconSet, $size }) =>
    $isIconSet &&
    $size === 'medium' &&
    css`
      margin-inline-start: 39px;
    `};

  ${({ $isIconSet, $size }) =>
    $isIconSet &&
    $size === 'large' &&
    css`
      margin-inline-start: 39px;
    `};

  ${Textarea}:focus ~ & {
    ${({ $isFilled, $hasFocus }) => ($isFilled || $hasFocus) && filledStyle};
  }

  ${({ $sizeConfined, $isFilled, $hasFocus, $isIconSet }) =>
    $sizeConfined &&
    css`
      @media ${mediaConfined} {
        ${sizes[$sizeConfined].label};
        ${$isFilled && filledStyle};
        ${$hasFocus && filledStyle};
        ${$isIconSet &&
        $sizeConfined === 'medium' &&
        'margin-inline-start: 39px'};
        ${$isIconSet &&
        $sizeConfined === 'large' &&
        'margin-inline-start: 39px'};
      }
    `};

  ${({ $sizeWide, $isFilled, $hasFocus, $isIconSet }) =>
    $sizeWide &&
    css`
      @media ${mediaWide} {
        ${sizes[$sizeWide].label};
        ${$isFilled && filledStyle};
        ${$hasFocus && filledStyle};
        ${$isFilled && $isIconSet && 'inset-inline-start: -25px'};
        ${$hasFocus && $isIconSet && 'inset-inline-start: -25px'};
      }
    `};
`;

export const ActionWrapper = styled.div<ActionWrapperTProps>`
  color: ${xdlColorTextSecondary};
  inset-block-start: 50%;
  inset-inline-end: ${spaceM};
  position: absolute;
  transform: translateY(-50%);

  ${(props) => css`
    ${props.$isDisabled &&
    css`
      pointer-events: none;

      & > *,
      & * {
        pointer-events: none !important;
      }

      & > button,
      & > a {
        color: ${xdlColorDisabledStrong};
      }
    `};

    ${props.$hasFocus &&
    css`
      & > button,
      & > a {
        color: ${xdlColorText};
      }
    `};
  `};
`;

// *** Icon ***
export const IconContainer = styled.span<IconContainerTProps>`
  color: ${xdlColorTextSecondary};
  inset-block-start: 50%;
  inset-inline-start: 10px;
  margin-inline-end: ${spaceS};
  position: absolute;
  transform: translateY(-50%);

  ${(props) => css`
    ${props.$iconSize &&
    css`
      height: ${props.$iconSize}px;
      width: ${props.$iconSize}px;
    `};

    ${props.$isDisabled &&
    css`
      && {
        color: ${xdlColorDisabledStrong};
      }
    `};

    ${props.$hasFocus &&
    css`
      color: ${xdlColorText};
    `};
  `};
`;
